import { useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { TeamPicker } from "./ContractTeamFields";
import { fmtDay } from "./contractUtils";

/** Side panel shown while the admin has dates selected on the grid. */
export function BlockBookPanel({ contract, dates, onClear, onBooked }) {
  const [override, setOverride] = useState(false);
  const [team, setTeam] = useState({ assignee_type: "", operative_id: "", operative_ids: [], vendor_id: "" });
  const [times, setTimes] = useState({ start_time: contract.start_time || "", end_time: contract.end_time || "" });
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const sorted = [...dates].sort();

  const book = async () => {
    setSaving(true);
    try {
      const body = { dates: sorted, note, notify_client: notify, start_time: times.start_time, end_time: times.end_time };
      if (override) Object.assign(body, { assignee_type: team.assignee_type || "none", assignee_id: team.assignee_type === "in_house" ? (team.operative_ids || [])[0] || team.operative_id : team.assignee_type === "supplier" ? team.vendor_id : "", assignee_ids: team.assignee_type === "in_house" ? (team.operative_ids || (team.operative_id ? [team.operative_id] : [])) : [] });
      const { data } = await API.post(`/contracts/${contract.id}/visits`, body);
      toast.success(data.message);
      onBooked(); onClear();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not book those dates"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-primary/40 bg-primary/5 rounded-lg p-4 space-y-3" data-testid="block-book-panel">
      <div className="flex items-start justify-between gap-2">
        <div><p className="text-sm font-semibold font-[Chivo]">Block-book {sorted.length} date{sorted.length !== 1 ? "s" : ""}</p><p className="text-[11px] text-muted-foreground">{fmtDay(sorted[0], { day: "numeric", month: "short" })}{sorted.length > 1 ? ` → ${fmtDay(sorted[sorted.length - 1], { day: "numeric", month: "short" })}` : ""} · click more days to add</p></div>
        <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground" onClick={onClear} data-testid="block-book-clear">Clear</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Arrive</Label><Input type="time" value={times.start_time} onChange={(e) => setTimes({ ...times, start_time: e.target.value })} data-testid="block-book-start" /></div>
        <div><Label className="text-xs">Finish</Label><Input type="time" value={times.end_time} onChange={(e) => setTimes({ ...times, end_time: e.target.value })} data-testid="block-book-end" /></div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs">Team: <span className="font-medium">{override ? "override below" : (contract.default_operative_name || contract.default_vendor_name || "unassigned (contract default)")}</span></p>
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground"><Switch checked={override} onCheckedChange={setOverride} data-testid="block-book-override" /> Override</label>
      </div>
      {override && <TeamPicker value={team} onChange={(p) => setTeam((t) => ({ ...t, ...p }))} testid="block-book-team" />}
      <div><Label className="text-xs">Note <span className="text-muted-foreground">(optional)</span></Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Deep clean before audit" data-testid="block-book-note" /></div>
      <label className="flex items-center gap-2 text-[11px] text-muted-foreground"><Switch checked={notify} onCheckedChange={setNotify} data-testid="block-book-notify" /> Tell the client's portal users</label>
      <Button className="w-full" onClick={book} disabled={saving} data-testid="block-book-submit">{saving ? "Booking…" : `Book ${sorted.length} visit${sorted.length !== 1 ? "s" : ""}`}</Button>
    </div>
  );
}
